(() => {
  const STORAGE_KEY = 'koota-a11y-preferences-v1';
  const MAX_ALT_LENGTH = 125;
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;
  const DECORATIVE_FILE_HINTS = [
    'background',
    'divider',
    'decor',
    'motif',
    'pattern',
    'shape',
    'spacer',
    'separator',
    'pafcaremonte',
  ];
  const ACTION_LABELS = {
    text: 'Texte agrandi',
    contrast: 'Contraste renforcé',
    links: 'Soulignement des liens',
    motion: 'Réduction des animations',
    captions: 'Préférence de sous-titres',
  };
  const CLASS_NAMES = {
    text: 'koota-a11y-text-large',
    contrast: 'koota-a11y-high-contrast',
    links: 'koota-a11y-links',
    motion: 'koota-a11y-reduce-motion',
  };

  const defaultPreferences = () => ({
    text: false,
    contrast: false,
    links: false,
    motion: motionQuery.matches,
    captions: false,
  });

  const loadPreferences = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      if (!stored || typeof stored !== 'object') return defaultPreferences();

      return Object.keys(defaultPreferences()).reduce((preferences, key) => {
        preferences[key] = typeof stored[key] === 'boolean' ? stored[key] : defaultPreferences()[key];
        return preferences;
      }, {});
    } catch (_error) {
      return defaultPreferences();
    }
  };

  let preferences = loadPreferences();

  const savePreferences = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (_error) {
      // The controls remain usable when storage is unavailable.
    }
  };

  const cleanText = (value = '') =>
    value
      .replace(/\s+/g, ' ')
      .replace(/\s+([,.;:!?])/g, '$1')
      .trim();

  const truncate = (value, maxLength = MAX_ALT_LENGTH) => {
    const text = cleanText(value);
    if (text.length <= maxLength) return text;

    const shortened = text.slice(0, maxLength + 1);
    const lastSpace = shortened.lastIndexOf(' ');
    return `${shortened.slice(0, Math.max(lastSpace, maxLength - 18)).trim()}…`;
  };

  const announce = (message) => {
    const liveRegion = document.querySelector('[data-koota-a11y-live]');
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 30);
  };

  const imageFileName = (image) => {
    try {
      return decodeURIComponent(new URL(image.currentSrc || image.src, window.location.href).pathname)
        .split('/')
        .pop()
        .replace(/\.[a-z0-9]+$/i, '')
        .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|\d+x?)$/i, '')
        .toLowerCase();
    } catch (_error) {
      return '';
    }
  };

  const pageKeyword = () => {
    const heading = cleanText(document.querySelector('main h1, h1')?.textContent || '');
    if (heading) return heading;

    const title = cleanText(document.title)
      .replace(/\s*[|–—-]\s*KOOTA(?:\s+CAFÉ)?\s*$/i, '')
      .replace(/\s*[|–—-]\s*KOOTACAFE\.COM\s*$/i, '');
    return title || 'Café bio KOOTA';
  };

  const isDecorativeImage = (image, fileName) => {
    if (image.closest('[aria-hidden="true"]')) return true;
    if (image.getAttribute('role') === 'presentation') return true;
    if (DECORATIVE_FILE_HINTS.some((hint) => fileName.includes(hint))) return true;

    return image.matches(
      '.placeholder-svg, .icon, [class*="__decor"], [class*="__motif"], [class*="__background"]'
    );
  };

  const hasPoorAlt = (image) => {
    if (!image.hasAttribute('alt')) return true;
    const alt = cleanText(image.getAttribute('alt') || '');
    if (!alt) return false;

    const normalized = alt.toLowerCase();
    const fileName = imageFileName(image);
    return (
      /__pid:|\.(?:avif|gif|jpe?g|png|svg|webp)(?:\b|_)/i.test(alt) ||
      /^(?:image|img|photo|picture|visuel|thumbnail|[\d_ .-]+)$/i.test(alt) ||
      (fileName && normalized.replace(/\.[a-z0-9]+$/i, '') === fileName)
    );
  };

  const traceabilityAlt = (fileName) => {
    if (fileName.includes('tasse') && fileName.includes('1666346645825')) {
      return 'Tasse de café avec QR code de traçabilité KOOTA';
    }

    const qrCodeStage = fileName.match(/qrcode([1-5])/i)?.[1];
    const labels = {
      1: 'Scanner le QR code de traçabilité du café KOOTA',
      2: 'Consulter l’origine du café grâce au QR code KOOTA',
      3: 'Consulter la récolte du café grâce au QR code KOOTA',
      4: 'Suivre l’acheminement et la transformation du café KOOTA',
      5: 'Voir les conseils de préparation et de recyclage KOOTA',
    };
    return labels[qrCodeStage] || '';
  };

  const localImageContext = (image) => {
    const ownTitle = cleanText(image.getAttribute('title') || '');
    if (ownTitle) return ownTitle;

    const container = image.closest(
      'figure, article, li, .card-wrapper, .product-card-wrapper, [data-product-card], [data-href], section'
    );
    if (!container) return '';

    const caption = cleanText(container.querySelector('figcaption')?.textContent || '');
    if (caption) return caption;
    const heading = cleanText(container.querySelector('h1, h2, h3, h4')?.textContent || '');
    if (heading) return heading;
    return cleanText(container.getAttribute('aria-label') || '');
  };

  const generateImageAlt = (image) => {
    if (!(image instanceof HTMLImageElement) || !hasPoorAlt(image)) return;

    const fileName = imageFileName(image);
    if (isDecorativeImage(image, fileName)) {
      image.alt = '';
      image.dataset.kootaAltGenerated = 'decorative';
      return;
    }

    const traceAlt = traceabilityAlt(fileName);
    if (traceAlt) {
      image.alt = traceAlt;
      image.dataset.kootaAltGenerated = 'contextual';
      return;
    }

    if (fileName.includes('logo')) {
      image.alt = 'KOOTA, café bio et équitable';
      image.dataset.kootaAltGenerated = 'contextual';
      return;
    }

    const localContext = localImageContext(image);
    const keyword = pageKeyword();
    const alreadyContextual =
      localContext &&
      keyword
        .toLocaleLowerCase('fr')
        .split(/\s+/)
        .filter((word) => word.length > 4)
        .some((word) => localContext.toLocaleLowerCase('fr').includes(word));
    const generated = localContext
      ? alreadyContextual
        ? localContext
        : `${localContext} — ${keyword}`
      : keyword;

    image.alt = truncate(generated || 'Café bio KOOTA');
    image.dataset.kootaAltGenerated = 'page-context';
  };

  const candidateElements = (scope, selector) => [
    ...(scope instanceof Element && scope.matches(selector) ? [scope] : []),
    ...(scope.querySelectorAll?.(selector) || []),
  ];

  const enhanceClickableContainers = (scope) => {
    candidateElements(scope, '[data-href]:not(a):not(button):not([data-koota-keyboard-link])').forEach((element) => {
      const href = element.dataset.href;
      if (!href) return;

      element.dataset.kootaKeyboardLink = 'true';
      if (element.querySelector('a[href]')) return;
      if (element.querySelector('button, input, select, textarea')) {
        const assistiveLink = document.createElement('a');
        const label = cleanText(element.querySelector('h1, h2, h3, h4, p')?.textContent || 'ce contenu');
        assistiveLink.className = 'koota-a11y-card-link';
        assistiveLink.href = href;
        assistiveLink.textContent = `Voir ${truncate(label, 80)}`;
        if (element.dataset.target === '_blank') assistiveLink.target = '_blank';
        element.prepend(assistiveLink);
        return;
      }

      element.setAttribute('role', 'link');
      element.setAttribute('tabindex', '0');
      if (!element.hasAttribute('aria-label')) {
        const label = cleanText(element.querySelector('h1, h2, h3, h4, p')?.textContent || '');
        if (label) element.setAttribute('aria-label', truncate(label, 90));
      }
      element.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (element.dataset.target === '_blank') {
          window.open(href, '_blank', 'noopener,noreferrer');
        } else {
          window.location.assign(href);
        }
      });
    });
  };

  const enhanceFormControls = (scope) => {
    candidateElements(
      scope,
      'input:not([type="hidden"]):not([type="submit"]):not([type="button"]), select, textarea'
    ).forEach((control) => {
      if (control.hasAttribute('aria-label') || control.hasAttribute('aria-labelledby')) return;
      if (control.closest('label')) return;
      if (control.id && document.querySelector(`label[for="${CSS.escape(control.id)}"]`)) return;

      const fallback = cleanText(
        control.getAttribute('placeholder') || control.getAttribute('title') || control.getAttribute('name') || ''
      );
      if (fallback) control.setAttribute('aria-label', fallback.replace(/[_-]+/g, ' '));
    });
  };

  const applyCaptionPreference = (scope, enabled = preferences.captions) => {
    candidateElements(scope, 'video').forEach((video) => {
      Array.from(video.textTracks || []).forEach((track) => {
        if (track.kind === 'captions' || track.kind === 'subtitles') {
          track.mode = enabled ? 'showing' : 'disabled';
        }
      });
    });

    candidateElements(scope, 'iframe[src*="youtube.com/embed"], iframe[src*="youtube-nocookie.com/embed"], iframe[src*="player.vimeo.com/video"]').forEach(
      (iframe) => {
        try {
          const url = new URL(iframe.src, window.location.href);
          if (url.hostname.includes('youtube')) {
            if (enabled) {
              url.searchParams.set('cc_load_policy', '1');
              url.searchParams.set('cc_lang_pref', document.documentElement.lang.split('-')[0] || 'fr');
            } else {
              url.searchParams.delete('cc_load_policy');
              url.searchParams.delete('cc_lang_pref');
            }
          } else {
            if (enabled) url.searchParams.set('texttrack', document.documentElement.lang.split('-')[0] || 'fr');
            else url.searchParams.delete('texttrack');
          }
          if (url.href !== iframe.src) iframe.src = url.href;
        } catch (_error) {
          // Ignore invalid URLs injected by third-party widgets.
        }
      }
    );
  };

  const enhanceMedia = (scope) => {
    candidateElements(scope, 'video').forEach((video) => {
      const decorative = Boolean(video.closest('[aria-hidden="true"], .kch__media'));
      if (video.autoplay && !video.muted) {
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.pause();
      }
      if (!decorative) video.controls = true;
      if (video.readyState >= 1) applyCaptionPreference(video);
      else video.addEventListener('loadedmetadata', () => applyCaptionPreference(video), { once: true });
    });

    candidateElements(scope, 'audio').forEach((audio) => {
      audio.controls = true;
      if (audio.autoplay) {
        audio.autoplay = false;
        audio.removeAttribute('autoplay');
        audio.pause();
      }
    });

    applyCaptionPreference(scope);
  };

  const pauseAnimatedComponents = (scope = document) => {
    candidateElements(
      scope,
      '[data-koota-pause], [data-kch-pause], [data-announcement-autoplay]'
    ).forEach((button) => {
      if (button.dataset.kootaA11yPaused === 'true') return;
      const label = cleanText(button.getAttribute('aria-label') || '').toLocaleLowerCase('fr');
      if (button.getAttribute('aria-pressed') === 'true' || label.includes('relancer') || label.includes('reprendre')) {
        return;
      }
      button.dataset.kootaA11yPaused = 'true';
      button.click();
    });
    candidateElements(scope, 'video').forEach((video) => video.pause());
  };

  const enhanceThirdPartyUi = (scope = document) => {
    candidateElements(
      scope,
      'button.chat-toggle:not([aria-label]), button.chat-toggle.mobile-only:not([aria-label]), button[class*="chat-toggle"]:not([aria-label])'
    ).forEach((button) => {
      button.setAttribute('aria-label', 'Ouvrir l’assistance Koota');
      button.setAttribute('title', 'Ouvrir l’assistance Koota');
    });

    candidateElements(scope, 'iframe:not([title])').forEach((iframe) => {
      iframe.setAttribute('title', 'Contenu interactif Koota');
    });
    candidateElements(scope, 'img').forEach(generateImageAlt);

    candidateElements(
      scope,
      '.jdgm-carousel__left-arrow:not([aria-label]), .splide__arrow--prev:not([aria-label])'
    ).forEach((button) => button.setAttribute('aria-label', 'Avis précédent'));
    candidateElements(
      scope,
      '.jdgm-carousel__right-arrow:not([aria-label]), .splide__arrow--next:not([aria-label])'
    ).forEach((button) => button.setAttribute('aria-label', 'Avis suivant'));

    candidateElements(scope, 'a[target="_blank"]').forEach((link) => {
      const rel = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
      rel.add('noopener');
      link.setAttribute('rel', Array.from(rel).join(' '));
      if (!link.hasAttribute('aria-describedby') && document.getElementById('a11y-new-window-message')) {
        link.setAttribute('aria-describedby', 'a11y-new-window-message');
      }
    });

    candidateElements(scope, 'a:not([href]):not([role="button"])').forEach((link) => {
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
    });

    enhanceClickableContainers(scope);
    enhanceFormControls(scope);
    enhanceMedia(scope);
    if (preferences.motion) pauseAnimatedComponents(scope);
  };

  const updateToolbar = () => {
    document.querySelectorAll('[data-koota-a11y-action]').forEach((button) => {
      const action = button.dataset.kootaA11yAction;
      button.setAttribute('aria-pressed', String(Boolean(preferences[action])));
    });
  };

  const applyPreferences = (changedAction = '') => {
    Object.entries(CLASS_NAMES).forEach(([preference, className]) => {
      root.classList.toggle(className, Boolean(preferences[preference]));
    });
    updateToolbar();
    if (preferences.motion) pauseAnimatedComponents();
    applyCaptionPreference(document);
    if (changedAction) {
      announce(`${ACTION_LABELS[changedAction]} ${preferences[changedAction] ? 'activé' : 'désactivé'}.`);
    }
  };

  const bindToolbar = () => {
    document.querySelectorAll('[data-koota-a11y-action]').forEach((button) => {
      button.addEventListener('click', () => {
        const action = button.dataset.kootaA11yAction;
        preferences[action] = !preferences[action];
        savePreferences();
        applyPreferences(action);
      });
    });

    document.querySelector('[data-koota-a11y-reset]')?.addEventListener('click', () => {
      preferences = defaultPreferences();
      savePreferences();
      applyPreferences();
      announce('Réglages d’accessibilité réinitialisés.');
    });
  };

  const start = () => {
    bindToolbar();
    applyPreferences();
    enhanceThirdPartyUi();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) enhanceThirdPartyUi(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    [3000, 8000, 15000].forEach((delay) => {
      window.setTimeout(() => enhanceThirdPartyUi(), delay);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
